import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { container } from '@/app/container';
import { CatalogRepositoryToken } from '../../di/tokens';
import type {
  PerkGroup,
  PerkHolderCount,
  PerkHolderCountGroup,
} from '../../domain/entities/catalog.entity';

/** A `key` estável de uma capacidade — a mesma que o `keyExtractor` da tabela usa. */
export function perkGroupKey(group: PerkGroup): string {
  return `${group.type}:${group.key}`;
}

/** Todos os `perkId` de uma capacidade (um por `grantPolicyKind`). */
export function perkGroupPerkIds(group: PerkGroup): string[] {
  return group.grantForms.map((form) => form.perkId);
}

/**
 * Contagem de portadores das capacidades da **página atual**, em uma chamada.
 * A chave de cache é a lista de `perkId` de todos os grupos (ordenada) — como
 * cada linha já carrega os seus, é a chave natural, sem cache novo no back.
 * Devolve um `Map<key, count>` para a tabela casar por linha.
 */
export function usePerkHolderCounts(groups: PerkHolderCountGroup[]) {
  const repository = container.resolve(CatalogRepositoryToken);
  const cacheKey = [...groups.flatMap((g) => g.perkIds)].sort().join(',');

  return useQuery({
    queryKey: ['catalog', 'perk-holder-counts', cacheKey],
    queryFn: async () => {
      const counts = await repository.countPerkHolders(groups);
      return new Map<string, number>(
        counts.map((c: PerkHolderCount) => [c.key, c.count]),
      );
    },
    enabled: groups.length > 0,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

/**
 * Uma página de portadores (ids) de uma linha + a resolução de exibição do
 * identity, numa query só: busca os ids paginados e, na sequência, hidrata
 * `name`/`email`/`scope`. `enabled` só quando há `perkIds` (o modal fechado não
 * dispara). A ordem da página do delivery é preservada.
 */
export function usePerkHolders(
  perkIds: string[],
  page: number,
  perPage: number,
) {
  const repository = container.resolve(CatalogRepositoryToken);

  return useQuery({
    queryKey: [
      'catalog',
      'perk-holders',
      [...perkIds].sort().join(','),
      page,
      perPage,
    ],
    queryFn: async () => {
      const pageResult = await repository.listPerkHolderProfiles(perkIds, {
        page,
        perPage,
      });
      const ids = pageResult.items.map((i) => i.platformProfileId);
      const resolved =
        ids.length > 0 ? await repository.resolvePlatformProfiles(ids) : [];
      const byId = new Map(resolved.map((r) => [r.platformProfileId, r]));

      return {
        items: ids.map(
          (id) =>
            byId.get(id) ?? {
              platformProfileId: id,
              userId: '',
              name: '—',
              email: null,
              scopeId: '',
            },
        ),
        total: pageResult.total,
        page: pageResult.page,
        perPage: pageResult.perPage,
      };
    },
    enabled: perkIds.length > 0,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}
